import { PureComponent } from "react";

import * as Sentry from "@sentry/react";
import axios, { type AxiosResponse } from "axios";

import {
  type Bar,
  type ChartingLibraryWidgetOptions,
  type DatafeedConfiguration,
  type IBasicDataFeed,
  type IChartingLibraryWidget,
  type LanguageCode,
  type LibrarySymbolInfo,
  type ResolutionString,
  type Timezone,
  widget,
} from "~/chart";
import instance from "~/v2/api";
import { MustReauthenticateError } from "~/v2/api/auth/MustReauthenticateError";
import { TRADING_VIEW_POLLING_INTERVALS } from "~/v2/constants";
import { UTC_TO_OLSON } from "./constants";
import { formatChartHistoryEndDate } from "./utils";

// Stop the poll loop on an auth failure. A dead session throws
// MustReauthenticateError from the request interceptor; a raw data-feed 401 is
// no longer wrapped (it does not opt into logoutOn401), so match it explicitly
// to avoid hammering the endpoint after it starts returning 401.
const isUnauthorizedPollError = (err: unknown): boolean =>
  err instanceof MustReauthenticateError ||
  (axios.isAxiosError(err) && err.response?.status === 401);

export interface ChartContainerProps {
  symbol: ChartingLibraryWidgetOptions["symbol"];
  interval: string;
  enabled_features?: ChartingLibraryWidgetOptions["enabled_features"];
  disabled_features?: ChartingLibraryWidgetOptions["disabled_features"];
  theme?: ChartingLibraryWidgetOptions["theme"];
  // BEWARE: no trailing slash is expected in feed URL
  datafeedUrl: string;
  libraryPath: ChartingLibraryWidgetOptions["library_path"];
  chartsStorageUrl: ChartingLibraryWidgetOptions["charts_storage_url"];
  chartsStorageApiVersion: ChartingLibraryWidgetOptions["charts_storage_api_version"];
  clientId: ChartingLibraryWidgetOptions["client_id"];
  userId: ChartingLibraryWidgetOptions["user_id"];
  fullscreen: ChartingLibraryWidgetOptions["fullscreen"];
  autosize: ChartingLibraryWidgetOptions["autosize"];
  studiesOverrides: ChartingLibraryWidgetOptions["studies_overrides"];
  container: string;
  height: string | number;
  width: string | number;
  /** Base URL for the stock data API (DATA_API_URL). Not used for crypto. */
  baseUrl: string;
  crypto: boolean;
  /** Optional override for crypto bar endpoints (e.g. us-1 marketcache). */
  cryptoDataApiUrl?: string;
  seriesStyle: number;
  utcOffset: string;
  exchange: string;
  /** Whether the user has a real-time market data subscription (controls 15-min paywall). */
  isUnlimited: boolean;
  /** Whether the symbol supports overnight trading (enables dual SIP+BOATS fetch). */
  isOvernightTradable: boolean;
}

interface BarData extends Bar {
  o: number;
  c: number;
  v: number;
  h: number;
  l: number;
  t: string;
}

function getLanguageFromURL(): LanguageCode | null {
  const regex = /[?&]lang=([^&#]*)/;
  const results = regex.exec(location.search);
  return results === null
    ? null
    : (decodeURIComponent(results[1].replace(/\+/g, " ")) as LanguageCode);
}

const ResMap: Record<string, string> = {
  "1": "1Min",
  "15": "15Min",
  "30": "30Min",
  "60": "1Hour",
  "1D": "1Day",
};

function formatBars(barData: BarData[] | null | undefined): BarData[] {
  if (!barData?.length) {
    return [];
  }
  return barData.map((bar) => ({
    ...bar,
    time: Date.parse(bar.t),
    open: bar.o,
    close: bar.c,
    volume: bar.v,
    high: bar.h,
    low: bar.l,
  }));
}

function deduplicateBars(bars: BarData[]): BarData[] {
  const seen = new Set<number>();
  return bars.filter((bar) => {
    if (seen.has(bar.time)) {
      return false;
    }
    seen.add(bar.time);
    return true;
  });
}

export class TVChartContainer extends PureComponent<
  Partial<ChartContainerProps>
> {
  static defaultProps: Partial<ChartContainerProps> = {
    symbol: "AAPL",
    interval: "15" as ResolutionString,
    container: "tv_chart_container",
    datafeedUrl: "https://demo_feed.tradingview.com",
    libraryPath: "chart/",
    chartsStorageUrl: "https://saveload.tradingview.com",
    chartsStorageApiVersion: "1.1",
    clientId: "tradingview.com",
    userId: "public_user_id",
    fullscreen: false,
    autosize: true,
    studiesOverrides: {},
    width: "100%",
    baseUrl: "https://data.tradetalk.us/v2",
    seriesStyle: 2,
    utcOffset: "+00:00",
    exchange: "NYSE",
    isUnlimited: false,
    isOvernightTradable: false,
  };

  private tvWidget: IChartingLibraryWidget | null = null;
  private clearAllPolling: (() => void) | null = null;

  componentDidMount() {
    const widgetOptions: ChartingLibraryWidgetOptions = {
      symbol: this.props.symbol,
      datafeed: this.createDataFeed(
        this.props.baseUrl as string,
        this.props.crypto as boolean,
        this.props.exchange as string,
        this.props.isUnlimited as boolean,
        this.props.isOvernightTradable as boolean,
        this.props.cryptoDataApiUrl ?? window.env.CRYPTO_DATA_API_URL,
      ),
      interval: this.props.interval as ChartingLibraryWidgetOptions["interval"],
      container: this.props.container as string,
      library_path: "chart/",
      locale: getLanguageFromURL() || "en",
      enabled_features: this.props.enabled_features,
      disabled_features: this.props.disabled_features,
      charts_storage_url: this.props.chartsStorageUrl,
      charts_storage_api_version: this.props.chartsStorageApiVersion,
      custom_css_url: "chart-custom.css",
      client_id: this.props.clientId,
      user_id: this.props.userId,
      fullscreen: this.props.fullscreen,
      autosize: this.props.autosize,
      // TradingView's generated types only allow number; runtime accepts CSS
      // sizes like "100%" which we pass from defaultProps / callers.
      height: this.props.height as ChartingLibraryWidgetOptions["height"],
      width: this.props.width as ChartingLibraryWidgetOptions["width"],
      theme: this.props.theme ?? "light",
      timezone: UTC_TO_OLSON[this.props.utcOffset as string] as Timezone,
      overrides: {
        "mainSeriesProperties.style": this.props.seriesStyle as number,
        "backgrounds.preMarket.color": "rgba(0, 0, 0, 0.03)",
        "backgrounds.postMarket.color": "rgba(0, 0, 0, 0.03)",
      },
    };
    this.tvWidget = new widget(widgetOptions);

    this.tvWidget.onChartReady(() => {
      if (this.tvWidget) {
        if (this.props.theme) {
          this.tvWidget.changeTheme(this.props.theme);
        }
        if (this.props.seriesStyle !== undefined) {
          this.tvWidget.chart().setChartType(this.props.seriesStyle);
        }
      }
    });
  }

  createDataFeed(
    baseUrl: string,
    crypto: boolean,
    exchange: string,
    isUnlimited: boolean,
    isOvernightTradable: boolean,
    cryptoDataApiUrl: string,
  ): ChartingLibraryWidgetOptions["datafeed"] {
    const datafeed = {} as IBasicDataFeed;
    const config = {
      supported_resolutions: ["1", "15", "30", "60", "1D"],
    } as DatafeedConfiguration;

    datafeed.onReady = (cb) => {
      setTimeout(() => cb(config), 0);
    };

    // Overnight-tradable stocks use the standard extended session (4am-8pm)
    // plus a separate overnight session (8pm-4am). TradingView's "0400-0400"
    // format caused the current trading day to not render, so we use an
    // explicit two-segment session instead.
    const session = crypto
      ? "24x7"
      : isOvernightTradable
        ? "0400-2000,2000-0400"
        : "0400-2000";

    datafeed.resolveSymbol = (symbolName, onSymbolResolvedCallback) => {
      const symbolInfo: LibrarySymbolInfo = {
        name: symbolName,
        description: "",
        type: "stock",
        session,
        timezone: "America/New_York",
        ticker: symbolName,
        minmov: 1,
        pricescale: 100,
        has_intraday: true,
        intraday_multipliers: ["1", "15", "30", "60"],
        supported_resolutions: [
          "1" as ResolutionString,
          "15" as ResolutionString,
          "30" as ResolutionString,
          "60" as ResolutionString,
          "1D" as ResolutionString,
        ],
        volume_precision: 8,
        data_status: "streaming",
        exchange,
        listed_exchange: exchange,
        format: "price",
        subsession_id: crypto ? undefined : "extended",
        subsessions: crypto
          ? undefined
          : [
              {
                description: "Regular Trading Hours",
                id: "regular",
                session: "0930-1600",
              },
              {
                description: "Extended Trading Hours",
                id: "extended",
                session,
              },
              {
                description: "Pre-market",
                id: "premarket",
                session: "0400-0930",
              },
              {
                description: "Post-market",
                id: "postmarket",
                session: isOvernightTradable
                  ? "1600-2000,2000-0400"
                  : "1600-2000",
              },
            ],
      };

      setTimeout(() => onSymbolResolvedCallback(symbolInfo), 0);
    };

    const pollIntervals = new Map<string, ReturnType<typeof setInterval>>();
    this.clearAllPolling = () => {
      pollIntervals.forEach(clearInterval);
      pollIntervals.clear();
    };

    datafeed.subscribeBars = (symbolInfo, resolution, onTick, listenerGuid) => {
      const resConverted = ResMap[resolution];
      const pollMs = TRADING_VIEW_POLLING_INTERVALS[resolution];
      if (!resConverted || !pollMs) {
        return;
      }
      if (!isUnlimited && !crypto) {
        return;
      }

      let consecutivePollErrors = 0;

      const poll = () => {
        const now = new Date().toISOString();
        const lookbackMs = Math.max(pollMs * 3, 120_000);
        const start = new Date(Date.now() - lookbackMs).toISOString();

        if (crypto) {
          const url = `${cryptoDataApiUrl}/bars?symbols=${symbolInfo.name}&start=${start}&end=${now}&timeframe=${resConverted}&limit=1&sort=desc`;
          instance
            .get(url)
            .then(({ data }) => {
              consecutivePollErrors = 0;
              const bars = formatBars(data?.bars?.[symbolInfo.name]);
              if (bars.length > 0) {
                onTick(bars[0]);
              }
            })
            .catch((err) => {
              if (isUnauthorizedPollError(err)) {
                const intervalId = pollIntervals.get(listenerGuid);
                if (intervalId) {
                  clearInterval(intervalId);
                  pollIntervals.delete(listenerGuid);
                }
                return;
              }
              consecutivePollErrors++;
              if (consecutivePollErrors >= 3) {
                Sentry.captureException(err, {
                  tags: { component: "tv-chart-poll", symbol: symbolInfo.name },
                });
              }
            });
          return;
        }

        const requests: Promise<AxiosResponse>[] = [
          instance.get(
            `${baseUrl}/stocks/${symbolInfo.name}/bars?start=${start}&end=${now}&timeframe=${resConverted}&adjustment=split&limit=1&sort=desc`,
          ),
        ];
        if (isOvernightTradable) {
          requests.push(
            instance.get(
              `${baseUrl}/stocks/${symbolInfo.name}/bars?start=${start}&end=${now}&timeframe=${resConverted}&adjustment=split&limit=1&sort=desc&feed=boats`,
            ),
          );
        }

        Promise.allSettled(requests).then((results) => {
          const fulfilled = results.filter(
            (r): r is PromiseFulfilledResult<AxiosResponse> =>
              r.status === "fulfilled",
          );

          if (fulfilled.length === 0) {
            const reasons = results
              .filter(
                (r): r is PromiseRejectedResult => r.status === "rejected",
              )
              .map((r) => r.reason);

            const hasUnauthorized = reasons.some(isUnauthorizedPollError);
            if (hasUnauthorized) {
              const intervalId = pollIntervals.get(listenerGuid);
              if (intervalId) {
                clearInterval(intervalId);
                pollIntervals.delete(listenerGuid);
              }
              return;
            }

            consecutivePollErrors++;
            if (consecutivePollErrors >= 3) {
              Sentry.captureException(
                new Error("Stock polling failed for all data sources"),
                {
                  tags: { component: "tv-chart-poll", symbol: symbolInfo.name },
                  extra: { reasons },
                },
              );
            }
            return;
          }

          consecutivePollErrors = 0;
          const bars = fulfilled
            .flatMap((r) => formatBars(r.value.data?.bars))
            .sort((a, b) => b.time - a.time);
          if (bars.length > 0) {
            onTick(bars[0]);
          }
        });
      };

      const existingIntervalId = pollIntervals.get(listenerGuid);
      if (existingIntervalId) {
        clearInterval(existingIntervalId);
        pollIntervals.delete(listenerGuid);
      }

      const intervalId = setInterval(poll, pollMs);
      pollIntervals.set(listenerGuid, intervalId);
      poll();
    };

    datafeed.unsubscribeBars = (listenerGuid) => {
      const intervalId = pollIntervals.get(listenerGuid);
      if (intervalId) {
        clearInterval(intervalId);
        pollIntervals.delete(listenerGuid);
      }
    };

    let consecutiveEmpty = 0;
    let lastResolution = "";

    datafeed.getBars = (
      symbolInfo,
      resolution,
      periodParams,
      onHistoryCallback,
      onErrorCallback,
    ) => {
      if (resolution !== lastResolution) {
        consecutiveEmpty = 0;
        lastResolution = resolution;
      }
      // Ensure we always fetch a meaningful chunk from the API so TradingView
      // doesn't have to paginate through gaps one tiny window at a time.
      // Overnight stocks need a larger window to clear 8pm-4am gaps in one hop.
      const minWindowMs =
        resolution === "1D"
          ? 0
          : isOvernightTradable
            ? 14 * 3_600_000
            : 6 * 3_600_000;
      const fromMs = Math.min(
        periodParams.from * 1000,
        periodParams.to * 1000 - minWindowMs,
      );
      const fromDate = new Date(fromMs).toISOString();

      const toDate = formatChartHistoryEndDate({
        periodToMs: periodParams.to * 1000,
        resolution,
        crypto,
        isUnlimited,
      });
      const resConverted = ResMap[resolution];

      const reportBars = (bars: ReturnType<typeof formatBars>) => {
        if (bars.length > 0) {
          consecutiveEmpty = 0;
          onHistoryCallback(bars, { noData: false });
        } else {
          consecutiveEmpty++;
          // Overnight stocks have larger gaps (overnight session + weekends)
          // so they need more attempts before giving up.
          const maxEmpty = isOvernightTradable ? 12 : 5;
          if (consecutiveEmpty >= maxEmpty) {
            Sentry.captureMessage(
              "Chart pagination exhausted empty window limit",
              {
                level: "warning",
                tags: {
                  component: "tv-chart-getBars",
                  symbol: symbolInfo.name,
                  resolution,
                  overnight: String(isOvernightTradable),
                },
                extra: {
                  consecutiveEmpty,
                  from: fromDate,
                  to: toDate,
                  periodFrom: new Date(periodParams.from * 1000).toISOString(),
                  periodTo: new Date(periodParams.to * 1000).toISOString(),
                },
              },
            );
            onHistoryCallback([], { noData: true });
          } else {
            // Skip back by the actual checked range (which includes the
            // minWindowMs extension), not TV's requested window. This avoids
            // re-checking time we already know is empty.
            const actualFromSec = fromMs / 1000;
            const checkedSec = periodParams.to - actualFromSec;
            onHistoryCallback([], {
              noData: true,
              nextTime: actualFromSec - Math.max(checkedSec, 300),
            });
          }
        }
      };

      if (crypto) {
        const url = `${cryptoDataApiUrl}/bars?symbols=${symbolInfo.name}&start=${fromDate}&end=${toDate}&timeframe=${resConverted}&limit=10000`;
        instance
          .get(url)
          .then(({ data }) => {
            const barData = data?.bars?.[symbolInfo.name];
            reportBars(formatBars(barData));
          })
          .catch((err) => {
            Sentry.captureException(err);
            onErrorCallback("Failed to load chart data");
          });
        return;
      }

      const buildStockUrl = (feed?: string) => {
        let url = `${baseUrl}/stocks/${symbolInfo.name}/bars?start=${fromDate}&end=${toDate}&timeframe=${resConverted}&adjustment=split&limit=10000`;
        if (feed) {
          url += `&feed=${feed}`;
        }
        return url;
      };

      const requests: Promise<AxiosResponse>[] = [
        instance.get(buildStockUrl()),
      ];
      if (isOvernightTradable && resolution !== "1D") {
        requests.push(instance.get(buildStockUrl("boats")));
      }

      Promise.allSettled(requests).then((results) => {
        const fulfilled = results.filter(
          (r): r is PromiseFulfilledResult<AxiosResponse> =>
            r.status === "fulfilled",
        );
        if (fulfilled.length === 0) {
          const firstErr = results.find(
            (r): r is PromiseRejectedResult => r.status === "rejected",
          );
          if (firstErr) {
            Sentry.captureException(firstErr.reason);
          }
          onErrorCallback("Failed to load chart data");
          return;
        }
        const bars = deduplicateBars(
          fulfilled
            .flatMap((r) => formatBars(r.value.data?.bars))
            .sort((a, b) => a.time - b.time),
        );
        reportBars(bars);
      });
    };

    return datafeed;
  }

  componentWillUnmount() {
    this.clearAllPolling?.();
    if (this.tvWidget !== null) {
      this.tvWidget.remove();
      this.tvWidget = null;
    }
  }

  public render(): JSX.Element {
    const height = this.props.height ?? 350;
    return (
      <div
        id={this.props.container}
        className="TVChartContainer"
        style={{
          height: typeof height === "number" ? `${height}px` : height,
          minHeight: typeof height === "number" ? `${height}px` : height,
          maxHeight: typeof height === "number" ? `${height}px` : height,
        }}
      />
    );
  }
}
